import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Loader2, MapPin, Calendar, Award, Heart,
  Shield, CheckCircle2, XCircle, AlertTriangle, Dog,
  ChevronRight, ChevronLeft, X, Phone, Mail, GitBranch, FileText, ExternalLink, Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import ApisAIFooter from '@/components/ApisAIFooter';
import { DogBadges, formatTitle } from '@/components/DogBadges';
import OptimizedImage from '@/components/OptimizedImage';

import { getImageUrl, PLACEHOLDER_IMAGE } from '@/utils/imageUrl';

import { API } from '@/utils/api';
import { useTranslation } from 'react-i18next';

// ==================== BREED HEALTH TESTS CONFIG ====================
// Each breed has its own set of health tests - THIS IS CORE PRODUCT LOGIC
const BREED_HEALTH_TESTS = {
  'Owczarek Niemiecki Krótkowłosy': [
    { key: 'hd', label: 'HD (biodra)' },
    { key: 'ed', label: 'ED (łokcie)' },
    { key: 'dm', label: 'DM (mielopatia)' },
    { key: 'spondylosis', label: 'Spondyloza' },
  ],
  'Owczarek Niemiecki Długowłosy': [
    { key: 'hd', label: 'HD (biodra)' },
    { key: 'ed', label: 'ED (łokcie)' },
    { key: 'dm', label: 'DM (mielopatia)' },
    { key: 'spondylosis', label: 'Spondyloza' },
  ],
  'Labrador Retriever': [
    { key: 'hd', label: 'HD (biodra)' },
    { key: 'ed', label: 'ED (łokcie)' },
    { key: 'eic', label: 'EIC' },
    { key: 'pra', label: 'PRA (oczy)' },
  ],
  'Golden Retriever': [
    { key: 'hd', label: 'HD (biodra)' },
    { key: 'ed', label: 'ED (łokcie)' },
    { key: 'pra', label: 'PRA (oczy)' },
    { key: 'ichthyosis', label: 'Ichthyosis' },
  ],
  'Buldog Francuski': [
    { key: 'boas', label: 'BOAS (oddech)' },
    { key: 'spine', label: 'Kręgosłup' },
    { key: 'heart', label: 'Serce' },
    { key: 'patella', label: 'Rzepki (PL)' },
  ],
  'Yorkshire Terrier': [
    { key: 'patella', label: 'Rzepki (PL)' },
    { key: 'eyes', label: 'Oczy' },
    { key: 'pss', label: 'Wątroba (PSS)' },
  ],
  'Pomeranian': [
    { key: 'patella', label: 'Rzepki (PL)' },
    { key: 'trachea', label: 'Tchawica' },
    { key: 'heart', label: 'Serce' },
  ],
  'Cavalier King Charles Spaniel': [
    { key: 'mvd', label: 'Serce (MVD)' },
    { key: 'sm', label: 'Syringomyelia (MRI)' },
    { key: 'eyes', label: 'Oczy' },
  ],
  'Border Collie': [
    { key: 'hd', label: 'HD (biodra)' },
    { key: 'ed', label: 'ED (łokcie)' },
    { key: 'cea', label: 'CEA (oczy)' },
    { key: 'pra', label: 'PRA (oczy)' },
  ],
  'Jack Russell Terrier': [
    { key: 'patella', label: 'Rzepki (PL)' },
    { key: 'eyes', label: 'Oczy (PLL, PRA)' },
    { key: 'ataxia', label: 'Ataksja' },
  ],
  'Doberman': [
    { key: 'dcm', label: 'Serce (DCM)' },
    { key: 'hd', label: 'HD (biodra)' },
    { key: 'vwd', label: 'vWD (krzepliwość)' },
  ],
  'Rottweiler': [
    { key: 'hd', label: 'HD (biodra)' },
    { key: 'ed', label: 'ED (łokcie)' },
    { key: 'heart', label: 'Serce' },
  ],
  'Owczarek Belgijski': [
    { key: 'hd', label: 'HD (biodra)' },
    { key: 'ed', label: 'ED (łokcie)' },
    { key: 'eyes', label: 'Oczy' },
  ],
  'Beagle': [
    { key: 'hd', label: 'HD (biodra)' },
    { key: 'ed', label: 'ED (łokcie)' },
    { key: 'eyes', label: 'Oczy' },
  ],
  'Shih Tzu': [
    { key: 'eyes', label: 'Oczy' },
    { key: 'patella', label: 'Rzepki (PL)' },
    { key: 'breathing', label: 'Drogi oddechowe' },
  ],
  'Cocker Spaniel': [
    { key: 'pra', label: 'Oczy (PRA)' },
    { key: 'hd', label: 'HD (biodra)' },
  ],
  'Siberian Husky': [
    { key: 'eyes', label: 'Oczy' },
    { key: 'hd', label: 'HD (biodra)' },
  ],
  'Berneński Pies Pasterski': [
    { key: 'hd', label: 'HD (biodra)' },
    { key: 'ed', label: 'ED (łokcie)' },
    { key: 'heart', label: 'Serce' },
  ],
  'Chihuahua': [
    { key: 'patella', label: 'Rzepki (PL)' },
    { key: 'heart', label: 'Serce' },
    { key: 'eyes', label: 'Oczy' },
  ],
  'Jamnik': [
    { key: 'ivdd', label: 'Kręgosłup (IVDD)' },
    { key: 'patella', label: 'Rzepki (PL)' },
    { key: 'eyes', label: 'Oczy' },
  ],
  'Akita': [
    { key: 'hd', label: 'HD (biodra)' },
    { key: 'eyes', label: 'Oczy' },
    { key: 'thyroid', label: 'Tarczyca' },
  ],
};

// Get health tests for a specific breed
const getBreedHealthTests = (breed) => {
  return BREED_HEALTH_TESTS[breed] || [];
};

// Theme configurations with Tailwind classes
const THEMES = {
  dark: { 
    bg: 'bg-[#0A0E1A]', 
    navBg: 'bg-[#0A0E1A]/95',
    text: 'text-white', 
    textMuted: 'text-gray-400', 
    border: 'border-white/10', 
    accent: 'text-amber-500',
    cardBg: 'bg-slate-800/50'
  },
  light: { 
    bg: 'bg-gray-50', 
    navBg: 'bg-white/95',
    text: 'text-gray-900', 
    textMuted: 'text-gray-500', 
    border: 'border-gray-200', 
    accent: 'text-amber-600',
    cardBg: 'bg-gray-100'
  },
  green: { 
    bg: 'bg-[#0A1F0A]', 
    navBg: 'bg-[#0A1F0A]/95',
    text: 'text-white', 
    textMuted: 'text-emerald-200/70', 
    border: 'border-emerald-500/20', 
    accent: 'text-emerald-400',
    cardBg: 'bg-emerald-900/30'
  },
  blue: { 
    bg: 'bg-[#0A0E1F]', 
    navBg: 'bg-[#0A0E1F]/95',
    text: 'text-white', 
    textMuted: 'text-sky-200/70', 
    border: 'border-sky-500/20', 
    accent: 'text-sky-400',
    cardBg: 'bg-blue-900/30'
  }
};

// Health test result badge
const HealthBadge = ({ result }) => {
  if (!result) return <span className="text-gray-500">—</span>;
  
  const isGood = ['A', 'A/A', '0', '0/0', 'Clear', 'Normal', 'Free'].some(
    g => result.toLowerCase().includes(g.toLowerCase())
  );
  const isWarning = ['B', 'B/B', '1', 'Carrier'].some(
    w => result.toLowerCase().includes(w.toLowerCase())
  );
  
  return (
    <Badge className={`${
      isGood ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' :
      isWarning ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
      'bg-red-500/20 text-red-400 border-red-500/30'
    }`}>
      {isGood && <CheckCircle2 className="w-3 h-3 mr-1" />}
      {isWarning && <AlertTriangle className="w-3 h-3 mr-1" />}
      {!isGood && !isWarning && <XCircle className="w-3 h-3 mr-1" />}
      {result}
    </Badge>
  );
};

// ==================== PEDIGREE TREE COMPONENT ====================
// Visual family tree display (3 generations) - SEO/AI-ready with text in DOM

const PedigreeTree = ({ pedigreeTree, pedigreeFile, pedigreeLink, theme }) => {
  // Check if we have valid tree data
  const hasSire = pedigreeTree?.sire?.name;
  const hasDam = pedigreeTree?.dam?.name;
  const hasTree = hasSire || hasDam;
  
  // If no tree data and no file/link, don't render
  if (!hasTree && !pedigreeFile && !pedigreeLink) return null;
  
  // Tree node component
  const TreeNode = ({ label, name, titles, gender, className = "" }) => {
    if (!name) return null;
    const isMale = gender === 'M' || label?.includes('♂') || label?.toLowerCase().includes('ojc') || label?.toLowerCase().includes('dziadek');
    const colorClass = isMale ? 'border-blue-500/30 bg-blue-500/10' : 'border-pink-500/30 bg-pink-500/10';
    const textColor = isMale ? 'text-blue-400' : 'text-pink-400';
    
    return (
      <div className={`p-3 rounded-lg border ${colorClass} ${className}`} data-testid={`pedigree-node-${label?.toLowerCase().replace(/[^a-z]/g, '-')}`}>
        <span className={`text-xs font-semibold ${textColor} block mb-1`}>{label}</span>
        <p className="text-white font-medium text-sm leading-tight">{name}</p>
        {titles && <p className="text-gray-400 text-xs mt-1">{titles}</p>}
      </div>
    );
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      className={`${theme.cardBg} rounded-2xl p-6 border ${theme.border}`}
      data-testid="pedigree-section"
    >
      <h2 className={`text-xl font-bold ${theme.text} mb-4 flex items-center gap-2`}>
        <GitBranch className="w-5 h-5 text-amber-400" />
        Rodowód
      </h2>
      
      {/* Visual Pedigree Tree - SEO/AI-ready text */}
      {hasTree && (
        <div className="mb-6">
          {/* Desktop: Horizontal tree layout */}
          <div className="hidden md:block">
            <div className="grid grid-cols-3 gap-4">
              {/* Generation 1: Parents */}
              <div className="space-y-3">
                <TreeNode 
                  label="♂ Ojciec (Sire)" 
                  name={pedigreeTree?.sire?.name} 
                  titles={pedigreeTree?.sire?.titles}
                  gender="M"
                />
                <TreeNode 
                  label="♀ Matka (Dam)" 
                  name={pedigreeTree?.dam?.name} 
                  titles={pedigreeTree?.dam?.titles}
                  gender="F"
                />
              </div>
              
              {/* Generation 2: Paternal grandparents */}
              <div className="space-y-3">
                <TreeNode 
                  label="♂ Dziadek (ojc.)" 
                  name={pedigreeTree?.sire?.sire?.name} 
                  titles={pedigreeTree?.sire?.sire?.titles}
                  gender="M"
                />
                <TreeNode 
                  label="♀ Babcia (ojc.)" 
                  name={pedigreeTree?.sire?.dam?.name} 
                  titles={pedigreeTree?.sire?.dam?.titles}
                  gender="F"
                />
                <TreeNode 
                  label="♂ Dziadek (mat.)" 
                  name={pedigreeTree?.dam?.sire?.name} 
                  titles={pedigreeTree?.dam?.sire?.titles}
                  gender="M"
                />
                <TreeNode 
                  label="♀ Babcia (mat.)" 
                  name={pedigreeTree?.dam?.dam?.name} 
                  titles={pedigreeTree?.dam?.dam?.titles}
                  gender="F"
                />
              </div>
              
              {/* Column labels */}
              <div className="flex flex-col justify-center items-center text-center">
                <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                  <p className="text-amber-400 text-sm font-semibold">3 Pokolenia</p>
                  <p className="text-gray-400 text-xs mt-1">Pochodzenie psa</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Mobile: Accordion/stacked layout */}
          <div className="md:hidden space-y-4">
            {/* Parents */}
            <div>
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2">Rodzice</p>
              <div className="grid grid-cols-2 gap-2">
                <TreeNode 
                  label="♂ Ojciec" 
                  name={pedigreeTree?.sire?.name} 
                  titles={pedigreeTree?.sire?.titles}
                  gender="M"
                />
                <TreeNode 
                  label="♀ Matka" 
                  name={pedigreeTree?.dam?.name} 
                  titles={pedigreeTree?.dam?.titles}
                  gender="F"
                />
              </div>
            </div>
            
            {/* Grandparents */}
            {(pedigreeTree?.sire?.sire?.name || pedigreeTree?.sire?.dam?.name || 
              pedigreeTree?.dam?.sire?.name || pedigreeTree?.dam?.dam?.name) && (
              <div>
                <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2">Dziadkowie</p>
                <div className="grid grid-cols-2 gap-2">
                  <TreeNode 
                    label="♂ Dz. (ojc.)" 
                    name={pedigreeTree?.sire?.sire?.name} 
                    titles={pedigreeTree?.sire?.sire?.titles}
                    gender="M"
                  />
                  <TreeNode 
                    label="♀ Ba. (ojc.)" 
                    name={pedigreeTree?.sire?.dam?.name} 
                    titles={pedigreeTree?.sire?.dam?.titles}
                    gender="F"
                  />
                  <TreeNode 
                    label="♂ Dz. (mat.)" 
                    name={pedigreeTree?.dam?.sire?.name} 
                    titles={pedigreeTree?.dam?.sire?.titles}
                    gender="M"
                  />
                  <TreeNode 
                    label="♀ Ba. (mat.)" 
                    name={pedigreeTree?.dam?.dam?.name} 
                    titles={pedigreeTree?.dam?.dam?.titles}
                    gender="F"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Action buttons: PDF download and external link */}
      {(pedigreeFile || pedigreeLink) && (
        <div className="flex flex-wrap gap-3 pt-4 border-t border-white/10">
          {pedigreeFile && (
            <a 
              href={getImageUrl(pedigreeFile)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded-lg text-sm font-medium transition-colors"
              data-testid="pedigree-download-btn"
            >
              <Download className="w-4 h-4" />
              Pobierz rodowód (PDF)
            </a>
          )}
          {pedigreeLink && (
            <a 
              href={pedigreeLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg text-sm font-medium transition-colors"
              data-testid="pedigree-external-link-btn"
            >
              <ExternalLink className="w-4 h-4" />
              Zobacz rodowód online
            </a>
          )}
        </div>
      )}
      
      {/* Fallback: No tree but has file/link - just show buttons message */}
      {!hasTree && (pedigreeFile || pedigreeLink) && (
        <p className="text-gray-400 text-sm mb-4">
          Pełne dane rodowodowe dostępne w dokumencie.
        </p>
      )}
    </motion.section>
  );
};

export default function PublicDog() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [allImages, setAllImages] = useState([]);

  useEffect(() => {
    fetchDog();
  }, [slug]);

  // Handle hash scrolling after page loads
  useEffect(() => {
    if (!loading && data) {
      const hash = window.location.hash;
      if (hash) {
        setTimeout(() => {
          const element = document.querySelector(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 300);
      }
    }
  }, [loading, data]);

  // Build all images array when data loads
  useEffect(() => {
    if (data?.dog) {
      const images = [];
      if (data.dog.main_photo) images.push(data.dog.main_photo);
      if (data.dog.images?.length) {
        data.dog.images.forEach(img => {
          if (img && !images.includes(img)) images.push(img);
        });
      }
      if (data.dog.gallery?.length) {
        data.dog.gallery.forEach(img => {
          if (img && !images.includes(img)) images.push(img);
        });
      }
      setAllImages(images);
    }
  }, [data]);

  const fetchDog = async () => {
    try {
      const res = await axios.get(`${API}/public/dog/${slug}`);
      setData(res.data);
    } catch (e) {
      setError('Pies nie znaleziony');
    } finally {
      setLoading(false);
    }
  };

  // Gallery navigation
  const openGallery = (index) => {
    setLightboxIndex(index);
    setLightboxImage(getImageUrl(allImages[index]));
  };

  const nextImage = (e) => {
    e.stopPropagation();
    const newIndex = (lightboxIndex + 1) % allImages.length;
    setLightboxIndex(newIndex);
    setLightboxImage(getImageUrl(allImages[newIndex]));
  };

  const prevImage = (e) => {
    e.stopPropagation();
    const newIndex = (lightboxIndex - 1 + allImages.length) % allImages.length;
    setLightboxIndex(newIndex);
    setLightboxImage(getImageUrl(allImages[newIndex]));
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxImage) return;
      if (e.key === 'ArrowRight') nextImage(e);
      if (e.key === 'ArrowLeft') prevImage(e);
      if (e.key === 'Escape') setLightboxImage(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxImage, lightboxIndex, allImages]);

  if (loading) {
    return (
      <div className={`min-h-screen ${THEMES.dark.bg} flex items-center justify-center`}>
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={`min-h-screen ${THEMES.dark.bg} flex items-center justify-center px-6`}>
        <div className="text-center">
          <Dog className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Pies nie znaleziony</h1>
          <p className="text-gray-400">Ten profil nie istnieje lub został usunięty.</p>
        </div>
      </div>
    );
  }

  const { dog, kennel } = data;
  const healthTests = dog.health_tests || {};
  const theme = THEMES[kennel?.theme] || THEMES.dark;
  
  // Calculate age
  const getAge = () => {
    if (!dog.birth_date) return null;
    const birth = new Date(dog.birth_date);
    const now = new Date();
    const years = Math.floor((now - birth) / (365.25 * 24 * 60 * 60 * 1000));
    const months = Math.floor(((now - birth) % (365.25 * 24 * 60 * 60 * 1000)) / (30.44 * 24 * 60 * 60 * 1000));
    if (years > 0) return `${years} lat${years === 1 ? '' : years < 5 ? 'a' : ''}`;
    return `${months} mies.`;
  };

  const genderLabel = dog.gender === 'female' ? 'Suczka' : 'Pies';
  const roleLabels = {
    breeding: dog.gender === 'female' ? 'Suczka hodowlana' : 'Reproduktor',
    stud: 'Reproduktor',
    retired: 'Na emeryturze',
    puppy: 'Szczeniak'
  };

  return (
    <>
      <Helmet>
        <title>{`${dog.name} — ${dog.breed}${kennel?.name ? ` | ${kennel.name}` : ''}`}</title>
        <meta name="description" content={`${dog.name} - ${roleLabels[dog.role] || genderLabel}. ${dog.breed}. ${dog.description?.slice(0, 150) || ''}`} />
      </Helmet>

      {/* Interactive Gallery Lightbox */}
      {lightboxImage && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/95 flex flex-col"
          onClick={() => setLightboxImage(null)}
        >
          {/* Top bar */}
          <div className="flex items-center justify-between p-4">
            <span className="text-white/70 text-sm">
              {lightboxIndex + 1} / {allImages.length}
            </span>
            <button 
              className="text-white hover:text-amber-400 transition-colors p-2"
              onClick={() => setLightboxImage(null)}
            >
              <X className="w-8 h-8" />
            </button>
          </div>

          {/* Main image area */}
          <div className="flex-1 flex items-center justify-center relative px-4">
            {/* Previous button */}
            {allImages.length > 1 && (
              <button 
                onClick={prevImage}
                className="absolute left-4 md:left-8 p-3 bg-black/50 hover:bg-amber-500/80 text-white rounded-full transition-all transform hover:scale-110 z-10"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            )}

            {/* Image */}
            <motion.img 
              key={lightboxIndex}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2 }}
              src={lightboxImage} 
              alt={dog.name} 
              className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Next button */}
            {allImages.length > 1 && (
              <button 
                onClick={nextImage}
                className="absolute right-4 md:right-8 p-3 bg-black/50 hover:bg-amber-500/80 text-white rounded-full transition-all transform hover:scale-110 z-10"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            )}
          </div>

          {/* Thumbnails strip */}
          {allImages.length > 1 && (
            <div className="p-4 overflow-x-auto">
              <div className="flex gap-2 justify-center">
                {allImages.map((img, i) => (
                  <button
                    key={i}
                    onClick={(e) => {
                      e.stopPropagation();
                      openGallery(i);
                    }}
                    className={`flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden transition-all ${
                      i === lightboxIndex 
                        ? 'ring-2 ring-amber-500 scale-105' 
                        : 'opacity-50 hover:opacity-100'
                    }`}
                  >
                    <img 
                      src={getImageUrl(img)} 
                      alt={`${dog.name} ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Keyboard hint */}
          <div className="text-center pb-4 text-white/40 text-xs hidden md:block">
            Użyj ← → do nawigacji • ESC aby zamknąć
          </div>
        </motion.div>
      )}

      <div className={`min-h-screen ${theme.bg}`}>
        {/* Navigation menu - same as main kennel page */}
        <nav className={`sticky top-0 z-50 ${theme.navBg} backdrop-blur-xl border-b ${theme.border}`}>
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex items-center justify-between h-16">
              {/* Logo / Kennel name */}
              {kennel && (
                <Link to={`/hodowla/${kennel.slug}`} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
                    <span className="text-white font-bold">{kennel.name?.charAt(0)}</span>
                  </div>
                  <span className={`${theme.text} font-bold text-lg hidden sm:block`}>{kennel.name}</span>
                </Link>
              )}
              
              {/* Navigation links */}
              <div className="flex items-center gap-6">
                <Link to={kennel ? `/hodowla/${kennel.slug}` : '/'} className={`${theme.textMuted} hover:${theme.text} text-sm transition-colors`}>
                  Home
                </Link>
                <Link to={kennel ? `/hodowla/${kennel.slug}#dogs` : '/'} className={`${theme.textMuted} hover:${theme.text} text-sm transition-colors`}>
                  Nasze psy
                </Link>
                <Link to={kennel ? `/hodowla/${kennel.slug}#gallery` : '/'} className={`${theme.textMuted} hover:${theme.text} text-sm transition-colors`}>
                  Galeria
                </Link>
                <Link to={kennel ? `/hodowla/${kennel.slug}#contact` : '/'} className={`${theme.textMuted} hover:${theme.text} text-sm transition-colors`}>
                  Kontakt
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero with main photo - smaller */}
        <section className="relative">
          <div className="aspect-[21/9] relative overflow-hidden">
            {(dog.main_photo || (dog.images && dog.images[0])) ? (
              <img 
                src={getImageUrl(dog.main_photo || dog.images[0])} 
                alt={dog.name}
                className="w-full h-full object-cover object-center cursor-pointer"
                onClick={() => setLightboxImage(getImageUrl(dog.main_photo || dog.images[0]))}
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-amber-900/30 to-amber-900/10 flex items-center justify-center">
                <Dog className="w-24 h-24 text-amber-500/30" />
              </div>
            )}
            <div className={`absolute inset-0 bg-gradient-to-t ${theme.name === 'light' ? 'from-gray-50 via-transparent to-transparent' : 'from-[#0A0E1A] via-transparent to-transparent'}`} />
          </div>
          
          {/* Name overlay */}
          <div className="absolute bottom-0 left-0 right-0">
            <div className="max-w-6xl mx-auto px-6 pb-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <h1 className={`text-3xl md:text-4xl font-bold ${theme.text} mb-1`}>{dog.name}</h1>
                <p className={`text-lg ${theme.textMuted} mb-3`}>
                  {dog.breed}
                  {dog.color && <span className="opacity-70"> • {dog.color}</span>}
                  {dog.birth_date && <span className="opacity-70"> • {new Date(dog.birth_date).toLocaleDateString('pl-PL')}</span>}
                  <span className="opacity-70"> • {dog.gender === 'female' ? '♀' : '♂'}</span>
                </p>
                
                {/* Titles/Championships as standardized badges - UPPERCASE */}
                {(dog.titles?.length > 0 || dog.training?.length > 0 || dog.health_tests_text) && (
                  <div className="mt-3">
                    <DogBadges 
                      titles={dog.titles || []}
                      training={dog.training || dog.selected_certificates || []}
                      healthTests={dog.health_tests_text || ''}
                      maxTitles={10}
                      maxTraining={10}
                      maxHealth={5}
                    />
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </section>

        <main className="max-w-6xl mx-auto px-6 py-8">
          
          {/* TOP SECTION: Gallery left + Description right */}
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            
            {/* LEFT: Gallery thumbnails */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-3"
            >
              {/* Main photo large */}
              {allImages.length > 0 && (
                <div 
                  className="aspect-[4/3] rounded-xl overflow-hidden cursor-pointer relative group"
                  onClick={() => openGallery(0)}
                >
                  <img 
                    src={getImageUrl(allImages[0])} 
                    alt={dog.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity text-sm bg-black/50 px-3 py-1 rounded-full">
                      Kliknij aby powiększyć
                    </span>
                  </div>
                </div>
              )}
              
              {/* Thumbnail row - show up to 4 */}
              {allImages.length > 1 && (
                <div className="grid grid-cols-4 gap-2">
                  {allImages.slice(1, 5).map((img, i) => (
                    <div 
                      key={i}
                      className="aspect-square rounded-lg overflow-hidden cursor-pointer relative group"
                      onClick={() => openGallery(i + 1)}
                    >
                      <img 
                        src={getImageUrl(img)} 
                        alt={`${dog.name} ${i + 2}`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      {/* Show "+X more" on last thumbnail if there are more images */}
                      {i === 3 && allImages.length > 5 && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                          <span className="text-white font-bold text-lg">
                            +{allImages.length - 5}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {/* Gallery info */}
              {allImages.length > 1 && (
                <p className={`text-sm ${theme.textMuted} text-center`}>
                  📷 {allImages.length} zdjęć • Kliknij aby otworzyć galerię
                </p>
              )}
            </motion.div>
            
            {/* RIGHT: Description - clean, no background */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-4"
            >
              {/* Pedigree name if exists */}
              {dog.pedigree_name && (
                <div>
                  <p className={`text-xs ${theme.textMuted} uppercase tracking-wider`}>Nazwa rodowodowa</p>
                  <p className="text-amber-400 font-medium">{dog.pedigree_name}</p>
                </div>
              )}
              
              {/* Pedigree number */}
              {dog.pedigree_number && (
                <div>
                  <p className={`text-xs ${theme.textMuted} uppercase tracking-wider`}>Nr rodowodu</p>
                  <p className="text-amber-400 font-mono">{dog.pedigree_number}</p>
                </div>
              )}
              
              {/* Description - plain text, no card */}
              {dog.description && (
                <div>
                  <p className={`text-xs ${theme.textMuted} uppercase tracking-wider mb-2`}>Opis</p>
                  <p className={`${theme.text} leading-relaxed whitespace-pre-line`}>{dog.description}</p>
                </div>
              )}
              
              {/* Stud info if available */}
              {dog.stud_available && (
                <div>
                  <p className={`text-xs ${theme.textMuted} uppercase tracking-wider`}>Krycie</p>
                  <p className="text-blue-400 font-medium">Dostępny do krycia</p>
                  {dog.stud_price && <p className={`${theme.text}`}>{dog.stud_price}</p>}
                </div>
              )}
            </motion.div>
          </div>

          {/* BOTTOM SECTIONS: Zdrowie, Wyszkolenie, Rodowód - without heavy backgrounds */}
          <div className="space-y-8 mt-8">
            
            {/* ZDROWIE - Health tests - clean list without heavy background */}
            {(() => {
              const breedTests = getBreedHealthTests(dog.breed);
              const completedTests = breedTests.filter(({ key }) => healthTests[key]);
              if (completedTests.length === 0) return null;
              
              return (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <p className={`text-xs ${theme.textMuted} uppercase tracking-wider mb-2`}>Zdrowie</p>
                  <div className="flex flex-wrap gap-2">
                    {completedTests.map(({ key, label }) => {
                      const result = healthTests[key];
                      const isGood = ['A', 'B', '0', 'N/N', 'clear', 'Prawidłowy'].includes(result);
                      return (
                        <span 
                          key={key} 
                          className={`text-sm ${isGood ? 'text-emerald-400' : 'text-amber-400'}`}
                        >
                          {isGood ? '✓' : '!'} {label}: {result}
                        </span>
                      );
                    })}
                  </div>
                </motion.div>
              );
            })()}

            {/* RODOWÓD - Pedigree tree */}
            <PedigreeTree 
              pedigreeTree={dog.pedigree_tree}
              pedigreeFile={dog.pedigree_file}
              pedigreeLink={dog.pedigree_link}
              theme={theme}
            />

          </div>
        </main>

        {/* ApisAI System Footer */}
        <ApisAIFooter pageType="dog" pageSlug={slug} />
      </div>
    </>
  );
}
